import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Select from "react-select";
import TextInput from "@components/form/TextInput";
import TextArea from "@components/form/TextArea";
import Label from "@components/form/Label";
import { useAppContext } from "@context/AppContext";
import Alert from "@components/shared/Alert";
import { capitalize } from "lodash";

interface HelpCenterModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Option = {
  value: string;
  label: string;
};

const HelpCenterModal: React.FunctionComponent<HelpCenterModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const { loggedInInfo } = useAppContext();
  const userEmail = loggedInInfo?.userDetails?.email || "";
  const userName = loggedInInfo?.userDetails?.name || "";
  const [formData, setFormData] = useState({
    subject: "",
    message: "",
    type: "feedback" as "feedback" | "question" | "error",
  });

  const typeOptions: Option[] = [
    { value: "feedback", label: t("help_center.feedback") },
    { value: "question", label: t("help_center.question") },
    { value: "error", label: t("help_center.error") },
  ];

  const handleSendEmail = (e: React.FormEvent) => {
    e.preventDefault();
    const to = "dev@waterledger.com";
    const subjectLine = `[CUSTOMER SERVICE REQUEST] ${capitalize(
      formData.type,
    )}: ${formData.subject}`;
    const body =
      `WaterLedger User: ${userName} <${userEmail}>\n` +
      `Reference URL: ${window.location.href}\n\n` +
      (formData.message ? `Your Message: ${formData.message}` : "");
    const mailtoLink = `mailto:${to}?subject=${encodeURIComponent(
      subjectLine,
    )}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
    setFormData({ subject: "", message: "", type: "feedback" });
    onClose();
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTypeChange = (selected: Option | null) => {
    if (selected) {
      setFormData(prev => ({
        ...prev,
        type: selected.value as "feedback" | "question" | "error",
      }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
          <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                {t("help_center.title")}
              </h3>
              <button
                type="button"
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-1 focus:ring-offset-2"
                onClick={onClose}
              >
                <span className="sr-only">{t("common.close")}</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>

            <div className="mb-4">
              <Alert type="info">
                <span className="font-semibold block mb-1">
                  {t("help_center.info")}
                </span>
                <span>
                  {t("help_center.manual_email_note", {
                    email: "dev@waterledger.com",
                  })}
                </span>
              </Alert>
            </div>

            <form onSubmit={handleSendEmail} className="space-y-4">
              <div>
                <Label htmlFor="type">{t("help_center.type")}</Label>
                <Select
                  id="type"
                  options={typeOptions}
                  value={typeOptions.find(
                    option => option.value === formData.type,
                  )}
                  onChange={handleTypeChange}
                  className="w-full"
                  classNamePrefix="react-select"
                  isClearable={false}
                  isSearchable={false}
                />
              </div>

              <div>
                <Label htmlFor="subject">{t("help_center.subject")}</Label>
                <TextInput
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  placeholder={t("help_center.subject_placeholder") as string}
                  required
                />
              </div>

              <div>
                <Label htmlFor="message">{t("help_center.message")}</Label>
                <TextArea
                  id="message"
                  name="message"
                  rows={6}
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder={t("help_center.message_placeholder") as string}
                  required
                />
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="submit"
                  disabled={!formData.subject || !formData.message}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto"
                >
                  {t("help_center.send_email")}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-outline-primary mt-3 sm:mt-0 sm:w-auto"
                >
                  {t("common.cancel")}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpCenterModal;
